import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Typography } from '@material-ui/core'
import { unwrapResult } from '@reduxjs/toolkit'
import IntlMessages from '@crema/utility/IntlMessages'
import RedButton from 'components/Button/RedButton'
import NormalTable from 'components/Table/NormalTable'
import StaffAssignmentTable from 'components/Temple/StaffAssignmentTable'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    selectCurrentTempleStaffAssignments,
    selectStaffAssignmentStatus,
    staffAssignmentDeleteThunk,
    staffAssignmentGenerateOccurrencesThunk,
    staffAssignmentListThunk,
} from 'store/reducers/temple/staffAssignmentSlice'
import { StaffPujaAssignmentResponseType } from 'types/api/temple'
import usePermissionCheck from 'hooks/usePermissionCheck'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'

const ListStaffAssignment: React.FC<RouteComponentProps> = ({ history, location }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const assignments = useAppSelector(selectCurrentTempleStaffAssignments)
    const api = useAppSelector(selectStaffAssignmentStatus)
    const permissions = usePermissionCheck(RESTRICTED_PATHS.STAFF_ASSIGNMENTS)
    const [deleteItem, setDeleteItem] = useState<StaffPujaAssignmentResponseType | null>(null)
    const [deletingIds, setDeletingIds] = useState<(string | number)[]>([])

    useEffect(() => {
        dispatch(staffAssignmentListThunk({}))
    }, [dispatch])

    const handleDelete = (canDelete?: boolean) => {
        if (canDelete && deleteItem) {
            setDeletingIds(prev => [...prev, deleteItem.id])
            dispatch(staffAssignmentDeleteThunk({ id: deleteItem.id }))
                .finally(() => {
                    setDeletingIds(prev => prev.filter(id => id !== deleteItem.id))
                    setDeleteItem(null)
                })
        } else {
            setDeleteItem(null)
        }
    }

    const handleGenerate = (id: string | number) => {
        dispatch(staffAssignmentGenerateOccurrencesThunk({ id }))
            .then(unwrapResult)
            .then(rows => enqueueSnackbar(`${rows.length} occurrences generated`, { variant: 'success' }))
            .then(() => dispatch(staffAssignmentListThunk({})))
            .catch(() => enqueueSnackbar('Unable to generate occurrences', { variant: 'error' }))
    }

    return (
        <Paper>
            <NormalTable
                api={api}
                toolbar={
                    <Box display='flex' justifyContent='space-between' p={5}>
                        <Typography variant='h6'>Staff Assignments</Typography>
                        {permissions.includes(PERMISSIONS.ADD) && (
                            <Button color='primary' variant='contained' onClick={() => history.push(`${location.pathname}/add`)}>
                                Add Assignment
                            </Button>
                        )}
                    </Box>
                }
            >
                <StaffAssignmentTable
                    assignments={assignments}
                    editHandler={permissions.includes(PERMISSIONS.CHANGE) ? id => history.push(`${location.pathname}/edit/${id}`) : undefined}
                    generateHandler={permissions.includes(PERMISSIONS.CHANGE) ? handleGenerate : undefined}
                    deleteHandler={permissions.includes(PERMISSIONS.DELETE) ? id => {
                        const item = assignments.find(row => row.id === id)
                        if (item) setDeleteItem(item)
                    } : undefined}
                    deletingIds={deletingIds}
                />
            </NormalTable>
            <Dialog open={Boolean(deleteItem)}>
                <DialogTitle>{`Delete assignment for ${deleteItem?.staffName}?`}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes the selected assignment and generated occurrences.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant='outlined' color='primary' onClick={() => handleDelete(false)}>
                        <IntlMessages id='common.cancel' />
                    </Button>
                    <RedButton variant='outlined' onClick={() => handleDelete(true)} autoFocus>
                        <IntlMessages id='common.delete' />
                    </RedButton>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default ListStaffAssignment
